import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRegisterItems, useTenancy } from '@/contexts/TenancyContext';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { handleNavigation } from '@/utils/navigation-utils';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { TourAlertDialog, useTour } from '@/components/tour';

export const Registers: React.FC = () => {
  const { registerItems, loading } = useRegisterItems();
  const { activeTenancy } = useTenancy();
  const navigate = useNavigate();
  
  // Tour state
  const { setSteps, isTourCompleted, setTourPage } = useTour();
  const [openTour, setOpenTour] = useState(false);

  // Tour implementation
  useEffect(() => {
    setTourPage('registers');
  }, [setTourPage]);

  // Tour configuration for Registers
  useEffect(() => {
    const steps: any[] = [
      {
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">Registers</h3>
            <p>This is your registers page. It shows you all the registers in your tenant.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.REGISTERS_PAGE,
        position: "bottom" as const,
      }
    ];

    // Add steps for visible register cards
    registerItems.forEach((item, index) => {
      let stepId = '';
      let stepTitle = '';
      
      switch(item.routeName) {
        case 'asset-register':
          stepId = TOUR_STEP_IDS.ASSET_REGISTER_CARD;
          stepTitle = 'Asset Register';
          break;
        case 'risk-register':
          stepId = TOUR_STEP_IDS.RISK_REGISTER_CARD;
          stepTitle = 'Risk Register';
          break;
        case 'supplier-register':
          stepId = TOUR_STEP_IDS.SUPPLIER_REGISTER_CARD;
          stepTitle = 'Supplier Register';
          break;
        case 'privacy-register':
          stepId = TOUR_STEP_IDS.PRIVACY_REGISTER_CARD;
          stepTitle = 'Privacy Register';
          break;
        case 'document-register':
          stepId = TOUR_STEP_IDS.DOCUMENT_REGISTER_CARD;
          stepTitle = 'Document Register';
          break;
        case 'assurancelibrary':
          stepId = TOUR_STEP_IDS.TASK_REGISTER_CARD;
          stepTitle = 'Task Register';
          break;
        default:
          return; // Skip if no matching ID
      }

      steps.push({
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">{stepTitle}</h3>
            <p>This is your {stepTitle.toLowerCase()} page. It shows you all the {stepTitle.toLowerCase()} in your tenant.</p>
          </div>
        ),
        selectorId: stepId,
        position: "right" as const,
      });
    });

    console.log('Registers tour steps:', steps.length);
    setSteps(steps);

    // Auto-start tour if not completed
    if (!isTourCompleted && !loading) {
      setOpenTour(true);
    }
  }, [setSteps, isTourCompleted, setTourPage, registerItems, loading]);

  return (
    <div id={TOUR_STEP_IDS.REGISTERS_PAGE} className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Registers</h1>
      {loading ? (
        <p className="text-muted-foreground">Loading registers...</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-1 md:max-xl:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {registerItems.map((item, idx) => {
            const Icon = item.icon;
            
            // Determine the ID based on routeName
            const cardId = 
              item.routeName === 'asset-register' ? TOUR_STEP_IDS.ASSET_REGISTER_CARD :
              item.routeName === 'risk-register' ? TOUR_STEP_IDS.RISK_REGISTER_CARD :
              item.routeName === 'supplier-register' ? TOUR_STEP_IDS.SUPPLIER_REGISTER_CARD :
              item.routeName === 'privacy-register' ? TOUR_STEP_IDS.PRIVACY_REGISTER_CARD :
              item.routeName === 'document-register' ? TOUR_STEP_IDS.DOCUMENT_REGISTER_CARD :
              item.routeName === 'assurancelibrary' ? TOUR_STEP_IDS.TASK_REGISTER_CARD :
              undefined;
            
            return (
              <Card
                key={idx}
                id={cardId}
                className="cursor-pointer transition-all duration-300 hover:shadow-md border border-border hover:border-primary/30 h-[6rem] overflow-hidden flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  if (item.url) {
                    // Use handleNavigation to support SPA navigation with routeName
                    handleNavigation(item.url, navigate, e, item.routeName);
                  }
                }}
              >
                <CardHeader className="flex flex-row items-center justify-center gap-4 px-4 w-full h-full">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-muted dark:bg-muted/40 flex items-center justify-center text-primary text-xl">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg md:text-lg sm:text-sm">{item.title}</CardTitle>
                    <CardDescription className="mt-1 text-sm md:text-sm sm:text-xs">{item.subtitle}</CardDescription>
                  </div>
                  <div className="flex-shrink-0 text-primary">
                    <i className="fa-duotone fa-arrow-right" aria-hidden="true" />
                  </div>
                </CardHeader>
              </Card>

            );
          })}
        </div>
      )}
      <TourAlertDialog 
        isOpen={openTour} 
        setIsOpen={setOpenTour} 
      />
    </div>
  );
};

export default Registers;
